import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Star, Rocket, Crown, Globe, Code, Users, ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const WebsitePackages = () => {
  const { t } = useTranslation();

  const packages = [
    {
      name: "Starter Website",
      subtitle: "Voor zzp'ers en kleine bedrijven",
      icon: <Globe className="w-8 h-8" weight="thin" />,
      price: "€49",
      period: "per maand",
      setupFee: "€0",
      description: "Perfect als je snel online wilt zijn zonder gedoe en lage instapkosten",
      features: [
        "Responsive website (past zich aan op mobiel & tablet)",
        "Maximaal 5 pagina's (bijv. Home, Over ons, Diensten, Contact, Privacy)",
        "Contactformulier met spamfilter",
        "Basis SEO optimalisatie (technisch + vindbaar in Google)",
        "SSL-certificaat (veilig en betrouwbaar)",
        "Hosting inbegrepen (geen extra kosten)",
        "Maandelijkse updates & onderhoud (beveiliging en techniek up-to-date)",
        "Email support (reactie binnen 2 werkdagen)"
      ],
      popular: false,
      color: "from-slate-600 to-slate-700",
      bgColor: "bg-slate-50",
      borderColor: "border-slate-200"
    },
    {
      name: "Business Pro",
      subtitle: "Voor bedrijven die willen groeien",
      icon: <Rocket className="w-8 h-8" weight="thin" />,
      price: "€99",
      period: "per maand",
      setupFee: "€0",
      description: "Ideaal als je online leads en klanten wilt genereren en actief bezig bent met marketing",
      features: [
        "Alles uit Starter Website, plus:",
        "Onbeperkt aantal pagina's (zo groot als je bedrijf groeit)",
        "Content Management System (CMS) → zelf teksten & afbeeldingen aanpassen",
        "Blog functionaliteit (voor nieuws, SEO en zichtbaarheid)",
        "Geavanceerde SEO (zoekwoorden, metadata, snelheid)",
        "Google Analytics integratie (inzichten in bezoekers & resultaten)",
        "Social media koppelingen (deel knoppen & feed integratie)",
        "Nieuwsbrief integratie (Mailchimp, ActiveCampaign of vergelijkbaar)",
        "Priority support (snelle reactie binnen 24 uur)"
      ],
      popular: true,
      color: "from-emerald-600 to-emerald-700",
      bgColor: "bg-emerald-50",
      borderColor: "border-emerald-300"
    },
    {
      name: "Custom Website",
      subtitle: "Voor complexe projecten",
      icon: <Code className="w-8 h-8" weight="thin" />,
      price: "Op maat",
      period: "prijs",
      setupFee: "€0",
      description: "Geschikt voor bedrijven die een serieuze online strategie willen neerzetten met complexe wensen of hoge eisen",
      features: [
        "Volledig op maat gemaakt design (100% uniek)",
        "Complexe functionaliteiten (maatwerk formulieren, dashboards, portals)",
        "Geavanceerde integraties (CRM, boekhouding, API-koppelingen)",
        "Webshop mogelijkheden (WooCommerce, Shopify of maatwerk)",
        "Custom CMS oplossingen (voor als standaard niet genoeg is)",
        "Database ontwerp & ontwikkeling",
        "Dedicated projectmanager (1 aanspreekpunt tijdens het traject)",
        "Uitgebreide testing (veiligheid & performance)",
        "Performance garanties (snelle laadtijden & schaalbaarheid)",
        "Strategisch advies (digitale strategie en groeiplan)",
        "Onderhoud & support na oplevering"
      ],
      popular: false,
      color: "from-purple-600 to-purple-700",
      bgColor: "bg-purple-50",
      borderColor: "border-purple-200",
      isCustom: true
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          {/* USP Badge */}
          <div className="inline-flex items-center bg-slate-100 text-slate-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Code className="w-4 h-4 mr-2" weight="fill" />
            Geen opstartkosten - Alleen maandelijkse abonnementen
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            Kies je <span className="text-slate-600">website pakket</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Transparante maandelijkse prijzen inclusief onderhoud en support. Van eenvoudige visitekaartje sites 
            tot complexe business websites - elk pakket groeit mee met je bedrijf.
          </p>
        </motion.div>

        {/* Packages Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto"
        >
          {packages.map((pkg, index) => (
            <motion.div
              key={pkg.name}
              variants={cardVariants}
              className={`relative ${pkg.bgColor} rounded-2xl p-8 border-2 ${pkg.borderColor} hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <div className="bg-gradient-to-r from-slate-600 to-slate-700 text-white px-6 py-2 rounded-full text-sm font-semibold shadow-lg">
                    <Star className="inline w-4 h-4 mr-2" weight="fill" />
                    Meest Gekozen
                  </div>
                </div>
              )}

              {/* Package Header */}
              <div className="text-center mb-8">
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${pkg.color} text-white rounded-full mb-4`}>
                  {pkg.icon}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{pkg.name}</h3>
                <p className="text-gray-600 mb-4">{pkg.subtitle}</p>
                <div className="mb-4">
                  <span className="text-4xl font-bold text-gray-900">{pkg.price}</span>
                  <span className="text-gray-500 ml-2">{pkg.period}</span>
                  {pkg.setupFee !== "€0" && (
                    <div className="text-sm text-gray-500 mt-1">
                      + {pkg.setupFee} setup
                    </div>
                  )}
                </div>
                <p className="text-gray-600 text-sm">{pkg.description}</p>
              </div>

              {/* Features List */}
              <div className="space-y-3 mb-8">
                {pkg.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" weight="fill" />
                    <span className="text-gray-700 text-sm">{feature}</span>
                  </div>
                ))}
              </div>

              {/* CTA Button */}
              <Link 
                to={pkg.isCustom ? "/contact" : "/start-your-project"} 
                className={`inline-block w-full bg-gradient-to-r ${pkg.color} text-white py-3 px-6 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 transform hover:scale-105 text-center`}
              >
                {pkg.isCustom ? "Vraag offerte aan" : `Start met ${pkg.name}`}
              </Link>
            </motion.div>
          ))}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          viewport={{ once: true }}
          className="text-center mt-16"
        >
          <div className="bg-gray-50 border border-gray-200 rounded-3xl p-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-500 text-white rounded-2xl mb-6">
              <Users className="w-8 h-8" weight="thin" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Weet je niet welk pakket past?</h3>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Neem contact op en we adviseren je vrijblijvend. Laat ons samen kijken naar jouw specifieke behoeften en budget. 
              We maken graag een op maat gemaakte oplossing die perfect bij je past.
            </p>
            <Link to="/contact" className="inline-flex items-center bg-blue-500 text-white px-10 py-4 rounded-2xl font-semibold hover:bg-gray-800 transition-colors">
              <span>Gratis Adviesgesprek</span>
              <ArrowRight className="w-5 h-5 ml-2" weight="thin" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WebsitePackages;
